import React, { useState, useEffect, useRef } from 'react';
import ReactToPrint from "react-to-print";
import { getMonthlySalesReport } from '../../../actions/report';
import {  getAllLocationPlain } from "../../../actions/warehouseAction";

import {
  Card,
  Row,
  Col,
  Select,
  Button,
  Table,
  Statistic,
  Alert,
  Spin,
  Space,
  Typography,
} from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';

const { Option } = Select;
const { Title } = Typography;

const SalesReportIndex = () => {
  // State for filters
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  
  const [reportData, setReportData] = useState(null);
  console.log(reportData)
  const [error, setError] = useState(null);
  
  const printRef = useRef();

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    setLoadingLocations(true);
    try {
      const dispatch = (action) => action; 
      const locationsData = await getAllLocationPlain()(dispatch);
      setLocations(locationsData || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Fetch report data
  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getMonthlySalesReport(year, month, location);
      setReportData(data);
    } catch (err) {
      setError(err.message || 'Failed to load sales report');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-BD', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const yearOptions = [...Array(5)].map((_, i) => {
    const y = new Date().getFullYear() - i;
    return { value: y, label: y };
  });

  // Month
  const monthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ].map((m, i) => ({ value: i + 1, label: m }));

  const getLocationName = () => {
    if (!location) return null;
    const selectedLocation = locations.find(loc => loc.id === location);
    return selectedLocation ? selectedLocation.name || selectedLocation.title : null;
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => formatDate(date),
      width: 150,
    },
    {
      title: 'Invoices',
      dataIndex: 'total_invoices',
      key: 'total_invoices',
      align: 'center',
      width: 100,
    },
    {
      title: 'Total Bill',
      dataIndex: 'total_bill',
      key: 'total_bill',
      align: 'right',
      render: (amount) => `${formatCurrency(amount)}`,
    },
    {
      title: 'Payments',
      dataIndex: 'total_payment',
      key: 'total_payment',
      align: 'right',
      render: (amount) => `${formatCurrency(amount)}`,
    },
    {
      title: 'Due',
      dataIndex: 'total_due',
      key: 'total_due',
      align: 'right',
      render: (amount) => `${formatCurrency(amount)}`,
    },
    {
      title: 'Quantity',
      dataIndex: 'total_quantity',
      key: 'total_quantity',
      align: 'center',
      width: 100,
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      {/* <Title level={2}>Monthly Sales Report</Title> */}
      
      {/* Filters Section */}
      <Card style={{ marginBottom: 24 }}>
        <Space size="middle" wrap>
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Year</div>
            <Select
              value={year}
              onChange={setYear}
              style={{ width: 120 }}
              options={yearOptions}
            />
          </div>

          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Month</div>
            <Select
              value={month}
              onChange={setMonth}
              style={{ width: 150 }}
              options={monthOptions}
            />
          </div>

          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Location</div>
            <Select
              value={location}
              onChange={setLocation}
              style={{ width: 250 }}
              placeholder="All Locations"
              allowClear
              loading={loadingLocations}
              showSearch
              filterOption={(input, option) =>
                (option && option.label ? option.label : '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {locations.map((loc) => (
                <Option key={loc.id} value={loc.id} label={loc.name || loc.title}>
                  <Space>
                    {/* <EnvironmentOutlined /> */}
                    {loc.name || loc.title || `Location ${loc.id}`}
                  </Space>
                </Option>
              ))}
            </Select>
          </div>

          <div style={{ paddingTop: 30 }}>
              <Space>
                  <Button
                      type="primary"
                      onClick={fetchReport}
                      loading={loading}
                  >
                      Generate Report
                  </Button>

                  {/* PDF Download Button */}
                  <ReactToPrint
                      trigger={() => (
                          <Button type="default">
                              Download PDF
                          </Button>
                      )}
                      content={() => printRef.current}
                      documentTitle={`Sales_Report_${year}_${month}`}
                  />
              </Space>
          </div>
        </Space>
      </Card>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 24 }}
        />
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" tip="Loading report..." />
        </div>
      )}

      {/* Report Data */}
      {!loading && reportData && (
        
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Invoices"
                    value={reportData.total_invoices}
                  //   prefix={<FileTextOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Sales"
                    value={formatCurrency(reportData.total_sales)}
                  //   prefix="৳"
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Payments"
                    value={formatCurrency(reportData.total_payments)}
                  //   prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Due"
                    value={formatCurrency(reportData.total_due)}
                  //   prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Daily Breakdown Table */}
            <Card
              title={
                <span>
                  Daily Breakdown - {reportData.month_name} {reportData.year}
                  {getLocationName() && ` - ${getLocationName()}`}
                </span>
              }
            >
              <div ref={printRef}>
              {reportData.daily_sales && reportData.daily_sales.length > 0 ? (
                <Table
                  columns={columns}
                  dataSource={reportData.daily_sales}
                  rowKey="date"
                  pagination={{
                    pageSize: 31,
                    showTotal: (total) => `Total ${total} days`,
                    showSizeChanger: false,
                  }}
                  scroll={{ x: 800 }}
                  summary={(pageData) => {
                    let totalInvoices = 0;
                    let totalBill = 0;
                    let totalPayment = 0;
                    let totalDue = 0;
                    let totalQuantity = 0;

                    pageData.forEach(({ total_invoices, total_bill, total_payment, total_due, total_quantity }) => {
                      totalInvoices += total_invoices;
                      totalBill += parseFloat(total_bill);
                      totalPayment += parseFloat(total_payment);
                      totalDue += parseFloat(total_due);
                      totalQuantity += total_quantity;
                    });

                    return (
                      <Table.Summary.Row style={{ fontWeight: 'bold', background: '#fafafa' }}>
                        <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="center">{totalInvoices}</Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="right">{formatCurrency(totalBill)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">{formatCurrency(totalPayment)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">{formatCurrency(totalDue)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="center">{totalQuantity}</Table.Summary.Cell>
                      </Table.Summary.Row>
                    );
                  }}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                  No sales data available for this period
                </div>
              )}
              </div>
            </Card>
          </>
        
      )}
    </div>
  );
};

export default SalesReportIndex;